<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('cashRegister.manageFlows') }}</span>
      </div>
    </div>

    <div class="ov-grid">
      <!-- ===== FORMULAIRE ===== -->
      <div class="ov-card">
        <div class="ov-card__head">
          <div>
            <div class="ov-card__title">Décaissement</div>
            <div class="ov-card__sub">{{ $t('cashRegister.fillFormDisbursement') }}</div>
          </div>
        </div>

        <div class="ov-card__body">
          <div class="ov-row">
            <!-- Référence gare -->
            <div class="ov-field ov-col-full">
              <label class="ov-label">
                {{ $t('cashRegister.stationReference') }}
                <span v-if="isAdminCompagnie" class="ov-required">*</span>
              </label>

              <!-- Admin compagnie : select des gares -->
              <select
                v-if="isAdminCompagnie"
                v-model="transactionToSend.data.gareDesignation"
                class="ov-select"
                @change="handleStationChange"
              >
                <option :value="null" disabled>{{ $t('cashRegister.selectStation') }}</option>
                <option
                  v-for="(gare, g) in gareList"
                  :value="gare.designation"
                  :key="g"
                >{{ gare.designation }}</option>
                <option value="__CREATE_NEW__" class="nt-option-create">{{ $t('cashRegister.createNewStation') }}</option>
              </select>

              <!-- Responsable gare : champ pré-rempli et grisé -->
              <input
                v-else
                disabled
                type="text"
                class="ov-input"
                :value="transactionToSend.data.gareDesignation"
              />
            </div>

            <!-- Motif -->
            <div class="ov-field ov-col-full">
              <label class="ov-label">
                {{ $t('cashRegister.transactionReason') }}
                <span class="ov-required">*</span>
              </label>
              <textarea
                v-model="transactionToSend.data.motifTransaction"
                class="ov-textarea"
                rows="3"
              ></textarea>
            </div>

            <!-- Type opération -->
            <div class="ov-field">
              <label class="ov-label">
                {{ $t('cashRegister.operationType') }}
                <span class="ov-required">*</span>
              </label>
              <select v-model="transactionToSend.data.typeOperation" class="ov-select">
                <option :value="null" disabled>{{ $t('cashRegister.selectOperationType') }}</option>
                <option
                  v-for="(type, t) in referenceTypeOperationList"
                  :value="type.designation"
                  :key="t"
                >{{ type.description }}</option>
              </select>
            </div>

            <!-- Type paiement -->
            <div class="ov-field">
              <label class="ov-label">
                {{ $t('cashRegister.paymentType') }}
                <span class="ov-required">*</span>
              </label>
              <select v-model="transactionToSend.data.typePaiement" class="ov-select">
                <option :value="null" disabled>{{ $t('cashRegister.selectPaymentType') }}</option>
                <option
                  v-for="(type, t) in referenceTypePaiementList"
                  :value="type.designation"
                  :key="t"
                >{{ type.description }}</option>
              </select>
            </div>

            <!-- Montant -->
            <div class="ov-field">
              <label class="ov-label">
                {{ $t('cashRegister.amount') }}
                <span class="ov-required">*</span>
              </label>
              <div class="ov-input-suffix">
                <input
                  v-model="transactionToSend.data.montantTransaction"
                  type="number"
                  min="0"
                  class="ov-input"
                  placeholder="0"
                />
                <span>FCFA</span>
              </div>
            </div>
          </div>
        </div>

        <div class="ov-card__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="resetForm">
            <v-icon small>mdi-refresh</v-icon>
            {{ $t('cashRegister.reset') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" :disabled="overlay" @click="createTransaction">
            <v-icon small>mdi-cash-minus</v-icon>
            {{ $t('cashRegister.disburse') }}
          </button>
        </div>
      </div>

      <!-- ===== GUIDE DE SAISIE ===== -->
      <div class="ov-sticky">
        <div class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">{{ $t('cashRegister.helpGuide.title') }}</div>
          </div>
          <div class="ov-card__body">
            <div class="nt-help">
              <div class="nt-help__title">{{ $t('cashRegister.helpGuide.stationReferenceTitle') }}</div>
              <div class="nt-help__desc">{{ $t('cashRegister.helpGuide.stationReferenceDesc') }}</div>
            </div>
            <div class="nt-help">
              <div class="nt-help__title">{{ $t('cashRegister.helpGuide.transactionReasonTitle') }}</div>
              <div class="nt-help__desc">{{ $t('cashRegister.helpGuide.transactionReasonDesc') }}</div>
            </div>
            <div class="nt-help">
              <div class="nt-help__title">{{ $t('cashRegister.helpGuide.operationTypeTitle') }}</div>
              <div class="nt-help__desc">{{ $t('cashRegister.helpGuide.operationTypeDesc') }}</div>
            </div>
            <div class="nt-help">
              <div class="nt-help__title">{{ $t('cashRegister.helpGuide.paymentModeTitle') }}</div>
              <div class="nt-help__desc">{{ $t('cashRegister.helpGuide.paymentModeDesc') }}</div>
            </div>
            <div class="ov-note ov-note--warning nt-help-warning">
              <v-icon small color="#b45309">mdi-alert-circle-outline</v-icon>
              <div>
                <strong>{{ $t('cashRegister.helpGuide.importantTitle') }}</strong>
                <div>{{ $t('cashRegister.helpGuide.importantDesc') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== DIALOGUE CRÉATION DE GARE ===== -->
    <v-dialog v-model="showCreateStationDialog" max-width="550px" persistent>
      <div class="ov-dialog">
        <div class="ov-dialog__head">
          <div class="ov-dialog__title">{{ $t('cashRegister.newStationDialog.title') }}</div>
        </div>
        <div class="ov-dialog__body">
          <div class="ov-row">
            <!-- Désignation -->
            <div class="ov-field ov-col-full">
              <label class="ov-label">
                {{ $t('cashRegister.newStationDialog.stationName') }}
                <span class="ov-required">*</span>
              </label>
              <input
                v-model="newStationData.designation"
                type="text"
                class="ov-input"
                :class="{ 'is-invalid': validationErrors.designation }"
                :placeholder="$t('cashRegister.newStationDialog.stationNamePlaceholder')"
              />
              <div class="ov-error" v-if="validationErrors.designation">{{ validationErrors.designation }}</div>
            </div>

            <!-- Email -->
            <div class="ov-field ov-col-full">
              <label class="ov-label">
                {{ $t('cashRegister.newStationDialog.email') }}
                <span class="ov-required">*</span>
              </label>
              <input
                v-model="newStationData.email"
                type="email"
                class="ov-input"
                :class="{ 'is-invalid': validationErrors.email }"
                :placeholder="$t('cashRegister.newStationDialog.emailPlaceholder')"
              />
              <div class="ov-error" v-if="validationErrors.email">{{ validationErrors.email }}</div>
            </div>

            <!-- Téléphone 1 -->
            <div class="ov-field">
              <label class="ov-label">
                {{ $t('cashRegister.newStationDialog.phone1') }}
                <span class="ov-required">*</span>
              </label>
              <div class="ov-input-group">
                <span class="ov-input-prefix">+225</span>
                <input
                  v-model="newStationData.telephone1"
                  type="text"
                  class="ov-input"
                  :class="{ 'is-invalid': validationErrors.telephone1 }"
                  :placeholder="$t('cashRegister.newStationDialog.phone1Placeholder')"
                />
              </div>
              <div class="ov-error" v-if="validationErrors.telephone1">{{ validationErrors.telephone1 }}</div>
            </div>

            <!-- Téléphone 2 (le libellé contient déjà « facultatif ») -->
            <div class="ov-field">
              <label class="ov-label">{{ $t('cashRegister.newStationDialog.phone2') }}</label>
              <div class="ov-input-group">
                <span class="ov-input-prefix">+225</span>
                <input
                  v-model="newStationData.telephone2"
                  type="text"
                  class="ov-input"
                  :placeholder="$t('cashRegister.newStationDialog.phone2Placeholder')"
                />
              </div>
            </div>

            <!-- Adresse -->
            <div class="ov-field ov-col-full">
              <label class="ov-label">
                {{ $t('cashRegister.newStationDialog.address') }}
                <span class="ov-required">*</span>
              </label>
              <input
                v-model="newStationData.adresseLocalisation"
                type="text"
                class="ov-input"
                :class="{ 'is-invalid': validationErrors.adresseLocalisation }"
                :placeholder="$t('cashRegister.newStationDialog.addressPlaceholder')"
              />
              <div class="ov-error" v-if="validationErrors.adresseLocalisation">{{ validationErrors.adresseLocalisation }}</div>
            </div>
          </div>
        </div>
        <div class="ov-dialog__foot">
          <button type="button" class="ov-btn ov-btn--secondary" :disabled="creatingStation" @click="cancelCreateStation">
            {{ $t('cashRegister.newStationDialog.cancel') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" :disabled="creatingStation" @click="createNewStation">
            <v-progress-circular v-if="creatingStation" indeterminate size="16" width="2"></v-progress-circular>
            <v-icon v-else small>mdi-check</v-icon>
            {{ creatingStation ? $t('cashRegister.newStationDialog.creating') : $t('cashRegister.newStationDialog.create') }}
          </button>
        </div>
      </div>
    </v-dialog>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import {
  API_CREATE_TRANSACTION_URL,
  API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
  API_RECUPERER_LISTE_GARES_PAR_COMPAGNIE,
  API_CREER_GARE_COMPAGNIE_TRANSPORT,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import axios from "axios";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../messages/messageProcess";
import { required } from "vuelidate/lib/validators";

export default {
  name: "NouvelleTransaction.vue",
  data() {
    return {
      errorMsg: null,
      successMsg: null,
      overlay: false,
      referenceTypePaiementList: [],
      referenceTypeOperationList: [],
      gareList: [],
      
      // Dialog de création de gare
      showCreateStationDialog: false,
      creatingStation: false,
      previousStationValue: null,
      newStationData: {
        designation: null,
        email: null,
        telephone1: null,
        telephone2: null,
        adresseLocalisation: null,
      },
      validationErrors: {
        designation: null,
        email: null,
        telephone1: null,
        adresseLocalisation: null,
      },

      referenceTypePaiement: {
        datas: [
          {
            referenceFamilleDesignation: "referenceFamilleTypePaiement",
          },
        ],
      },

      referenceTypeOperation: {
        datas: [
          {
            referenceFamilleDesignation: "referenceFamilleTypeOperation",
          },
        ],
      },

      transactionToSend: {
        data: {
          typePaiement: null,
          typeOperation: null,
          montantTransaction: null,
          motifTransaction: null,
          gareDesignation: null,
        },
      },
    };
  },

  validations: {
    transactionToSend: {
      data: {
        typePaiement: {
          required,
        },
        typeOperation: {
          required,
        },
      },
    },
  },

  computed: {
    isAdminCompagnie() {
      return this.$store.state.userAuthentified.roleCode === 'RoleAdminCompagnieTransport';
    },
  },

  methods: {
    handleStationChange(event) {
      const value = event.target.value;
      if (value === '__CREATE_NEW__') {
        this.showCreateStationDialog = true;
        // Restaurer la valeur précédente temporairement
        this.$nextTick(() => {
          this.transactionToSend.data.gareDesignation = this.previousStationValue || null;
        });
      } else {
        this.previousStationValue = value;
      }
    },

    cancelCreateStation() {
      this.showCreateStationDialog = false;
      this.resetStationForm();
    },

    resetStationForm() {
      this.newStationData = {
        designation: null,
        email: null,
        telephone1: null,
        telephone2: null,
        adresseLocalisation: null,
      };
      this.validationErrors = {
        designation: null,
        email: null,
        telephone1: null,
        adresseLocalisation: null,
      };
    },

    validateStationForm() {
      let isValid = true;
      this.validationErrors = {
        designation: null,
        email: null,
        telephone1: null,
        adresseLocalisation: null,
      };

      // Validation désignation
      if (!this.newStationData.designation || this.newStationData.designation.trim() === '') {
        this.validationErrors.designation = this.$t('cashRegister.newStationDialog.errors.designationRequired');
        isValid = false;
      }

      // Validation email
      if (!this.newStationData.email || this.newStationData.email.trim() === '') {
        this.validationErrors.email = this.$t('cashRegister.newStationDialog.errors.emailRequired');
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.newStationData.email)) {
        this.validationErrors.email = this.$t('cashRegister.newStationDialog.errors.emailInvalid');
        isValid = false;
      }

      // Validation téléphone 1
      if (!this.newStationData.telephone1 || this.newStationData.telephone1.trim() === '') {
        this.validationErrors.telephone1 = this.$t('cashRegister.newStationDialog.errors.phone1Required');
        isValid = false;
      }

      // Validation adresse
      if (!this.newStationData.adresseLocalisation || this.newStationData.adresseLocalisation.trim() === '') {
        this.validationErrors.adresseLocalisation = this.$t('cashRegister.newStationDialog.errors.addressRequired');
        isValid = false;
      }

      return isValid;
    },

    async createNewStation() {
      // Valider le formulaire
      if (!this.validateStationForm()) {
        return;
      }

      this.creatingStation = true;
      const stationData = {
        datas: [{
          designation: this.newStationData.designation.trim(),
          email: this.newStationData.email.trim(),
          telephone1: this.newStationData.telephone1.trim(),
          telephone2: this.newStationData.telephone2 ? this.newStationData.telephone2.trim() : null,
          adresseLocalisation: this.newStationData.adresseLocalisation.trim(),
          compagnieTransportRaisonSociale: this.$store.state.userAuthentified.compagnieTransportRaisonSociale
        }]
      };

      try {
        const response = await axios.post(
          API_CREER_GARE_COMPAGNIE_TRANSPORT,
          stationData,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        );

        if (response.data.status && response.data.status.code == 800) {
          this.successMsg = this.$t('cashRegister.newStationDialog.successMessage');
          showSuccessMessage();
          
          // Recharger la liste des gares
          await this.getGaresByCompagnie();
          
          // Sélectionner la nouvelle gare
          this.transactionToSend.data.gareDesignation = this.newStationData.designation.trim();
          this.previousStationValue = this.newStationData.designation.trim();
          
          // Fermer le dialog et réinitialiser
          this.showCreateStationDialog = false;
          this.resetStationForm();
        } else {
          this.errorMsg = response.data.status.message || this.$t('cashRegister.newStationDialog.errorMessage');
          showErrorMessage();
        }
      } catch (e) {
        this.errorMsg = this.$t('cashRegister.newStationDialog.errorMessage');
        showErrorMessage();
      } finally {
        this.creatingStation = false;
      }
    },

    resetForm() {
      this.transactionToSend.data.motifTransaction = null;
      this.transactionToSend.data.typeOperation = null;
      this.transactionToSend.data.typePaiement = null;
      this.transactionToSend.data.montantTransaction = null;
      if (this.isAdminCompagnie) {
        this.transactionToSend.data.gareDesignation = null;
        this.previousStationValue = null;
      }
    },

    async getGaresByCompagnie() {
      await axios
        .post(
          API_RECUPERER_LISTE_GARES_PAR_COMPAGNIE,
          { data: { compagnieTransportRaisonSociale: this.$store.state.userAuthentified.compagnieTransportRaisonSociale } },
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          if (response.data.status && response.data.status.code == 800) {
            this.gareList = response.data.items || [];
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // Obtenir la liste des reférences par type paiements
    async getReferenceTypePaiement() {
      await axios
        .post(
          API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
          this.referenceTypePaiement,
          {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          }
        )
        .then((response) => {
          this.referenceTypePaiementList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // Obtenir la liste des reférences par type opérations
    async getReferenceTypeOperation() {
      await axios
        .post(
          API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
          this.referenceTypeOperation,
          {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          }
        )
        .then((response) => {
          this.referenceTypeOperationList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // Valider le formulaire de transaction
    validateTransactionForm() {
      const errors = [];

      // Validation gare (pour admin compagnie)
      if (this.isAdminCompagnie && (!this.transactionToSend.data.gareDesignation || this.transactionToSend.data.gareDesignation === '')) {
        errors.push(this.$t('cashRegister.validationErrors.selectStation'));
      }

      // Validation motif
      if (!this.transactionToSend.data.motifTransaction || this.transactionToSend.data.motifTransaction.trim() === '') {
        errors.push(this.$t('cashRegister.validationErrors.reasonRequired'));
      }

      // Validation type opération
      if (!this.transactionToSend.data.typeOperation || this.transactionToSend.data.typeOperation === '') {
        errors.push(this.$t('cashRegister.validationErrors.operationTypeRequired'));
      }

      // Validation type paiement
      if (!this.transactionToSend.data.typePaiement || this.transactionToSend.data.typePaiement === '') {
        errors.push(this.$t('cashRegister.validationErrors.paymentTypeRequired'));
      }

      // Validation montant
      if (!this.transactionToSend.data.montantTransaction || this.transactionToSend.data.montantTransaction <= 0) {
        errors.push(this.$t('cashRegister.validationErrors.amountRequired'));
      }

      if (errors.length > 0) {
        this.errorMsg = errors.join('. ');
        showErrorMessage();
        return false;
      }

      return true;
    },

    // Créer une transaction
    async createTransaction() {
      // Valider le formulaire
      if (!this.validateTransactionForm()) {
        return;
      }

      this.overlay = true;
      await axios
        .post(API_CREATE_TRANSACTION_URL, this.transactionToSend, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            
            // Réinitialiser le formulaire après succès
            this.resetForm();
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e.response?.data?.status?.message || e.message || 'Erreur lors de la création de la transaction';
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
        });
    },
  },

  mounted() {
    this.getReferenceTypePaiement();
    this.getReferenceTypeOperation();
    if (this.isAdminCompagnie) {
      this.getGaresByCompagnie();
    } else {
      this.transactionToSend.data.gareDesignation =
        this.$store.state.userAuthentified.gareDesignation;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

/* Guide de saisie */
.nt-help {
  padding: 12px 0;
  border-bottom: 1px solid $ov-line;

  &:first-child {
    padding-top: 0;
  }
}

.nt-help__title {
  font-size: 13px;
  font-weight: 800;
  color: $ov-navy;
  margin-bottom: 3px;
}

.nt-help__desc {
  font-size: 12px;
  line-height: 1.5;
  color: $ov-muted;
}

.nt-help-warning {
  margin-top: 16px;
  font-size: 12px;

  strong {
    display: block;
    margin-bottom: 2px;
  }
}

.nt-option-create {
  font-weight: 700;
  color: $ov-green;
}

/* Le dialogue est rendu hors de .ov-page */
.ov-dialog {
  font-family: "Montserrat", sans-serif;

  button,
  input {
    font-family: "Montserrat", sans-serif;
  }
}
</style>
